import { ChartSvg, ChartTitle } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { estimateTextWidth } from "@/lib/tool-kit/charts/text";
import { CHART_INK } from "@/lib/tool-kit/palette";
import {
  BOX_HEIGHT,
  BRANCH_FONT,
  NODE_FONT,
  OUTCOME_FONT,
  type TreeLayout,
} from "@/lib/tool-kit/stats-distributions-more/tree-layout";

/*
 * A probability tree from `layoutTree`. Edges are drawn first, under the boxes; their
 * probabilities sit on small white plates so a crossing line never cuts a number. The
 * path to every selected outcome is drawn in the chosen colour and heavier, and the
 * outcome itself is bold on a tint of that colour. Colours are hex values, because an
 * exported SVG or PNG has no stylesheet.
 */

const HIGHLIGHT_OPACITY = 0.18;

export function TreeDiagram({
  id,
  layout,
  color,
  label,
  description,
}: {
  id: string;
  layout: TreeLayout;
  /** Colour of the selected paths. */
  color: string;
  label: string;
  description: string;
}) {
  return (
    <ChartSvg
      description={description}
      height={layout.height}
      id={id}
      label={label}
      width={layout.width}
    >
      <ChartTitle title={layout.title} />
      {layout.edges.map((edge) => (
        <line
          key={`edge-${edge.id}`}
          stroke={edge.onSelectedPath ? color : CHART_INK.axis}
          strokeWidth={edge.onSelectedPath ? 2.5 : 1.25}
          x1={px(edge.x1)}
          x2={px(edge.x2)}
          y1={px(edge.y1)}
          y2={px(edge.y2)}
        />
      ))}
      {layout.outcomes.map((outcome) => (
        <line
          key={`leader-${outcome.id}`}
          stroke={CHART_INK.grid}
          strokeDasharray="2 3"
          strokeWidth={1}
          x1={px(outcome.fromX)}
          x2={px(outcome.textX - 6)}
          y1={px(outcome.y)}
          y2={px(outcome.y)}
        />
      ))}
      <circle
        cx={px(layout.root.x)}
        cy={px(layout.root.y)}
        fill={CHART_INK.text}
        r={layout.root.r}
      />
      {layout.edges.map((edge) => {
        const midX = (edge.x1 + edge.x2) / 2;
        const midY = (edge.y1 + edge.y2) / 2;
        const plateWidth = edge.labelWidth + 8;
        return (
          <g key={`label-${edge.id}`}>
            <rect
              fill={CHART_INK.background}
              height={15}
              width={px(plateWidth)}
              x={px(midX - plateWidth / 2)}
              y={px(midY - 7.5)}
            />
            <text
              fill={CHART_INK.text}
              fontSize={BRANCH_FONT.fontSize}
              textAnchor="middle"
              x={px(midX)}
              y={px(midY + BRANCH_FONT.fontSize * 0.35)}
            >
              {edge.label}
            </text>
          </g>
        );
      })}
      {layout.nodes.map((node) => (
        <g key={`node-${node.id}`}>
          <rect
            fill={CHART_INK.background}
            height={BOX_HEIGHT}
            rx={4}
            stroke={node.onSelectedPath ? color : CHART_INK.axis}
            strokeWidth={node.onSelectedPath ? 2 : 1}
            width={px(node.width)}
            x={px(node.x)}
            y={px(node.y - BOX_HEIGHT / 2)}
          />
          <text
            fill={CHART_INK.text}
            fontSize={NODE_FONT.fontSize}
            textAnchor="middle"
            x={px(node.x + node.width / 2)}
            y={px(node.y + NODE_FONT.fontSize * 0.35)}
          >
            {node.label}
          </text>
        </g>
      ))}
      {layout.outcomes.map((outcome) => (
        <g key={`outcome-${outcome.id}`}>
          {outcome.selected ? (
            <rect
              fill={color}
              fillOpacity={HIGHLIGHT_OPACITY}
              height={22}
              rx={3}
              width={px(
                estimateTextWidth(outcome.text, {
                  ...OUTCOME_FONT,
                  bold: true,
                }) + 8
              )}
              x={px(outcome.textX - 4)}
              y={px(outcome.y - 11)}
            />
          ) : null}
          <text
            fill={CHART_INK.text}
            fontSize={OUTCOME_FONT.fontSize}
            fontWeight={outcome.selected ? "bold" : undefined}
            x={px(outcome.textX)}
            y={px(outcome.y + OUTCOME_FONT.fontSize * 0.35)}
          >
            {outcome.text}
          </text>
        </g>
      ))}
    </ChartSvg>
  );
}
